import {
    AnimationBaseController,
    IAnimationControllerConfiguration,
    Inject,
    ModelEvents,
    SoundType,
} from '@denishussar/html5-game-engine';
import {AnimationContents} from '../../../AnimationContents';
import {ButtonController, IButtonControllerOptions} from '../../Common/ButtonController';
import {LocalizationContents} from '../../LocalizationContents';
import {GameLogic} from '../../Logic/GameLogic';
import {BoosterTypeEnum, GameModel} from '../../Models/GameModel';
import {ITopBarMenuControllerOptions, TopBarMenuController} from '../Screens/TopBarMenuController';
import {ModalsNames} from '../../Common/MadalsManager/ModalsList';
import {ConfirmationsYouRewardType} from './Confirmations/ConfirmationsYouRewardController';
import {BalanceShopItemTypeEnum} from '../../Common/BalanceManager/IBalanceManager';
import {BlueButtonTextStyle, GreenButtonTextStyle, YellowButtonTextStyle} from '../../Constants/TextStyles';
import {AnalyticsController} from '../../Services/Analytics/AnalyticsController';
import {SavesManager} from '../../Managers/SavesManager';
import {GameEvent, GameEventStoreButtonClick, GameEventStoreExit, GameEventStoreOpen,} from '../../Constants/GameEvent';
import {IConfirmationsConfirmControllerOptions} from "./Confirmations/ConfirmationsConfirmController";
import {AdsLogic} from "../../Logic/Ads/AdsLogic";
import {REWARDED_ADS_TYPE} from "../../Logic/Ads/BaseAdsLogic";

export enum ShopCrazyGamesModalControllerOpenReason {
    yarn = 'Out of yarn',
    needle = 'Out of needle',
}

export interface IShopCrazyGamesModalControllerOptions {
    reason: ShopCrazyGamesModalControllerOpenReason;
}

export class ShopCrazyGamesModalController extends AnimationBaseController {
    $model: GameModel;

    buyingInProcess: boolean = false;

    actionsCount = 0;
    itemsCount = 0;

    protected buttonNeedles: ButtonController;
    protected buttonWool: ButtonController;
    protected buttonBucket: ButtonController;
    protected buttonCombo: ButtonController;
    protected timerId;

    public constructor(
        @Inject private options: IShopCrazyGamesModalControllerOptions,
        @Inject private gameLogic: GameLogic,
        @Inject private adsLogic: AdsLogic,
        @Inject private analytics: AnalyticsController,
        @Inject private saveManager: SavesManager,
        @Inject private modelEvents: ModelEvents,
        @Inject private model: GameModel,
    ) {
        super('ShopCrazyGamesModalController');
    }

    private topbar: TopBarMenuController;

    protected getConfiguration(): IAnimationControllerConfiguration {
        return <IAnimationControllerConfiguration>{
            animationContent: AnimationContents.SHOP_CRAZY_GAMES_JSON,
            libraryName: 'shop_crazygames_container',
            modelWatch: {
                picture: async () => {
                    this.updateButtons();
                },
            },
            childControllers: {
                topBarMenu: {
                    controller: TopBarMenuController,
                    model: this.$model,
                    options: <ITopBarMenuControllerOptions>{
                        title: LocalizationContents.getText('Store'),
                        showCloseButton: true,
                        showCoins: true,
                        showSettings: false,
                        onClickExit: (status: boolean) => {
                            this.modelEvents.dispatch(this.model, GameEvent.storeButtonClick, <GameEventStoreButtonClick>{
                                buttonName: 'Exit',
                                status,
                            });

                            this.hideConfirm();
                            this.hideReward();
                            this.gameLogic.modalsManager.hide();
                        },
                    },
                },
                showNeedles: {
                    instanceName: 'shop_crazygames.show_needles',
                    controller: ButtonController,
                    options: <IButtonControllerOptions>{
                        disabled: false,
                        onClick: (status: boolean) => {
                            this.modelEvents.dispatch(this.model, GameEvent.storeButtonClick, <GameEventStoreButtonClick>{
                                buttonName: 'Needle Icon',
                                status,
                            });

                            if (!status) {
                                return;
                            }

                            this.handleClickShowNeedles();
                        },
                        label: LocalizationContents.getText('Receive!'),
                        style: GreenButtonTextStyle,
                    },
                },
                showWool: {
                    instanceName: 'shop_crazygames.show_wool',
                    controller: ButtonController,
                    options: <IButtonControllerOptions>{
                        disabled: false,
                        onClick: (status: boolean) => {
                            this.modelEvents.dispatch(this.model, GameEvent.storeButtonClick, <GameEventStoreButtonClick>{
                                buttonName: 'Yarn Icon',
                                status,
                            });

                            if (!status) {
                                return;
                            }

                            this.handleClickShowWool();
                        },
                        label: LocalizationContents.getText('Receive!'),
                        style: GreenButtonTextStyle,
                    },
                },
                showBucket: {
                    instanceName: 'shop_crazygames.show_bucket',
                    controller: ButtonController,
                    options: <IButtonControllerOptions>{
                        disabled: false,
                        onClick: (status: boolean) => {
                            this.modelEvents.dispatch(this.model, GameEvent.storeButtonClick, <GameEventStoreButtonClick>{
                                buttonName: 'Bucket Icon',
                                status,
                            });

                            if (!status) {
                                return;
                            }

                            this.handleClickShowBucket();
                        },
                        label: LocalizationContents.getText('Receive!'),
                        style: GreenButtonTextStyle,
                    },
                },
                showCombo: {
                    instanceName: 'shop_crazygames.show_combo',
                    controller: ButtonController,
                    options: <IButtonControllerOptions>{
                        disabled: false,
                        onClick: (status: boolean) => {
                            this.modelEvents.dispatch(this.model, GameEvent.storeButtonClick, <GameEventStoreButtonClick>{
                                buttonName: 'Combo Ad',
                                status,
                            });

                            if (!status) {
                                return;
                            }

                            this.handleClickShowCombo();
                        },
                        label: LocalizationContents.getText('Receive!'),
                        style: GreenButtonTextStyle,
                    },
                },
                buy_combo: {
                    instanceName: 'shop_crazygames.buy_combo',
                    controller: ButtonController,
                    options: <IButtonControllerOptions>{
                        disabled: !this.gameLogic.canBuyShopItem(BalanceShopItemTypeEnum.combo),
                        label: this.gameLogic.balanceManager.getShopItemPrice(BalanceShopItemTypeEnum.combo).toString(),
                        style: BlueButtonTextStyle,
                        onClick: (status: boolean) => {
                            this.modelEvents.dispatch(this.model, GameEvent.storeButtonClick, <GameEventStoreButtonClick>{
                                buttonName: 'Big Combo Buy',
                                status,
                            });

                            if (!status) {
                                return;
                            }

                            this.handleClickBuyCombo();
                        },
                    },
                },
            },
        };
    }

    protected onDestroy(): void {
        clearTimeout(this.timerId);
    }

    private timerUpdate(): void {
        if (!this.buttonWool || !this.buttonNeedles || !this.buttonBucket || !this.buttonCombo) {
            return;
        }

        if (this.buyingInProcess) {
            this.buttonWool.disabled();
            this.buttonNeedles.disabled();
            this.buttonCombo.disabled();
            this.buttonBucket.disabled();

        } else {
            if (this.adsLogic.adsLogic.isAvailableRewardedAd(REWARDED_ADS_TYPE.SHOP_WOOL)) {
                this.buttonWool.label = LocalizationContents.getText('Receive!');

                if (!this.buyingInProcess) {
                    this.buttonWool.enabled();
                }
            } else {
                this.buttonWool.disabled();
                this.timerUpdateReward(REWARDED_ADS_TYPE.SHOP_WOOL, this.buttonWool);
            }

            if (this.adsLogic.adsLogic.isAvailableRewardedAd(REWARDED_ADS_TYPE.SHOP_NEEDLES)) {
                this.buttonNeedles.label = LocalizationContents.getText('Receive!');

                if (!this.buyingInProcess) {
                    this.buttonNeedles.enabled();
                }
            } else {
                this.buttonNeedles.disabled();
                this.timerUpdateReward(REWARDED_ADS_TYPE.SHOP_NEEDLES, this.buttonNeedles);
            }

            if (this.adsLogic.adsLogic.isAvailableRewardedAd(REWARDED_ADS_TYPE.SHOP_BUCKET)) {
                this.buttonBucket.label = LocalizationContents.getText('Receive!');

                if (!this.buyingInProcess) {
                    this.buttonBucket.enabled();
                }
            } else {
                this.buttonBucket.disabled();
                this.timerUpdateReward(REWARDED_ADS_TYPE.SHOP_BUCKET, this.buttonBucket);
            }

            if (this.adsLogic.adsLogic.isAvailableRewardedAd(REWARDED_ADS_TYPE.SHOP_COMBO)) {
                this.buttonCombo.label = LocalizationContents.getText('Receive!');

                if (!this.buyingInProcess) {
                    this.buttonCombo.enabled();
                }
            } else {
                this.buttonCombo.disabled();
                this.timerUpdateReward(REWARDED_ADS_TYPE.SHOP_COMBO, this.buttonCombo);
            }
        }

        this.timerId = setTimeout(() => this.timerUpdate(), 1000);
    }

    private timerUpdateReward(type: REWARDED_ADS_TYPE, button: ButtonController): void {
        const date = new Date(this.adsLogic.adsLogic.timeoutBeforeRewardedAd(type));
        const hours = Math.min(3, date.getUTCHours());
        const minutes = date.getMinutes();
        const seconds = date.getSeconds();

        const label = `${hours}:${minutes < 10 ? '0' + minutes : minutes}:${seconds < 10 ? '0' + seconds : seconds}`;
        button.label = label
    }

    protected onInitialize() {
        this.buyingInProcess = false;

        const profit = this.getDisplayObjectByQuery('shop_crazygames.profit')[0] as PIXI.Container;
        profit.children.forEach((item) => {
            if (item['text'] != undefined) {
                item['text'] = LocalizationContents.getText('Once at level');
            }
        });

        this.topbar = this.$childControllers.get('topBarMenu');
        this.setTextStyleByQuery('shop_crazygames.label', YellowButtonTextStyle);

        this.updateButtons();
    }

    public init(): void {
        this.actionsCount = 0;
        this.itemsCount = 0;

        this.model.storeCount++;

        this.modelEvents.dispatch(this.model, GameEvent.storeOpen, <GameEventStoreOpen>{
            yarnCount: this.model.userBalanceBoosterBrush,
            needleCount: this.model.userBalanceBoosterLine,
            bucketCount: this.model.userBalanceBoosterBucket,
            triggered: true,
            channel: this.options.reason,
        });

        this.buttonWool = this.$childControllers.get<ButtonController>('showWool');
        this.buttonNeedles = this.$childControllers.get<ButtonController>('showNeedles');
        this.buttonCombo = this.$childControllers.get<ButtonController>('showCombo');
        this.buttonBucket = this.$childControllers.get<ButtonController>('showBucket');

        if (this.adsLogic.isReady) {
            this.timerUpdate();
        }

        this.updateButtons();
    }

    public release() {
        this.modelEvents.dispatch(this.model, GameEvent.storeExit, <GameEventStoreExit>{
            actionsCount: this.actionsCount,
            itemsCount: this.itemsCount,
            channel: this.options.reason,
        });
        clearTimeout(this.timerId);
    }

    private updateButtons(): void {
        const buy_combo = this.$childControllers.get<ButtonController>('buy_combo');
        if (buy_combo) {
            if (this.buyingInProcess) {
                buy_combo.disabled();
            } else {
                if (!this.gameLogic.canBuyShopItem(BalanceShopItemTypeEnum.combo)) {
                    buy_combo.disabled();
                } else {
                    buy_combo.enabled();
                }
            }
        }

        const showNeedles = this.$childControllers.get<ButtonController>('showNeedles');
        if (showNeedles) {
            if (this.buyingInProcess) {
                showNeedles.disabled();
            } else {
                if (this.adsLogic.isReady ? this.adsLogic.adsLogic.isAvailableRewardedAd(REWARDED_ADS_TYPE.SHOP_NEEDLES) : true) {
                    showNeedles.enabled();
                } else {
                    this.timerUpdate();
                }
            }
        }

        const showWool = this.$childControllers.get<ButtonController>('showWool');
        if (showWool) {
            if (this.buyingInProcess) {
                showWool.disabled();
            } else {
                if (this.adsLogic.isReady ? this.adsLogic.adsLogic.isAvailableRewardedAd(REWARDED_ADS_TYPE.SHOP_WOOL) : true) {
                    showWool.enabled();
                } else {
                    this.timerUpdate();
                }
            }
        }

        const showBucket = this.$childControllers.get<ButtonController>('showBucket');
        if (showBucket) {
            if (this.buyingInProcess) {
                showBucket.disabled();
            } else {
                if (this.adsLogic.isReady ? this.adsLogic.adsLogic.isAvailableRewardedAd(REWARDED_ADS_TYPE.SHOP_BUCKET) : true) {
                    showBucket.enabled();
                } else {
                    this.timerUpdate();
                }
            }
        }

        const showCombo = this.$childControllers.get<ButtonController>('showCombo');
        if (showCombo) {
            if (this.buyingInProcess) {
                showCombo.disabled();
            } else {
                if (this.onceAtLevelSpended()) {
                    showCombo.disabled();
                } else {
                    if (this.adsLogic.isReady ? this.adsLogic.adsLogic.isAvailableRewardedAd(REWARDED_ADS_TYPE.SHOP_COMBO) : true) {
                        showCombo.enabled();
                    } else {
                        this.timerUpdate();
                    }
                }
            }
        }
    }

    private onceAtLevelSpended(): boolean {
        const result = this.saveManager.getCustomData('shopCrazy');
        let json;
        try {
            json = JSON.parse(result);
            return json['onceAtLevel'][this.gameLogic.getPicture().id] === 1;
        } catch (error) {
            return false;
        }
    }

    private saveOnceAtLevel() {
        let result = JSON.parse(this.saveManager.getCustomData('shopCrazy'));
        if (!result) {
            result = {};
        }
        if (!result['onceAtLevel']) {
            result['onceAtLevel'] = {};
        }
        result['onceAtLevel'][this.gameLogic.getPicture().id] = 1;

        this.saveManager.saveCustomData('shopCrazy', JSON.stringify(result));
    }

    protected onResize(width: number, height: number, scale: number) {
        const contentScale = Math.min(width / scale / (1920*1.2), height / scale / 1440);

        this.setScaleByQuery('shop_crazygames', contentScale, contentScale);

        // const margin = 20;
        //
        // const topLeftPoint = this.display.toLocal(new Point(0, 0), this.renderer.stage);
        // const bottomRightPoint = this.display.toLocal(new Point(width, height), this.renderer.stage);

        // const btnExit = this.$childControllers.get('btn_exit').display;
        // btnExit.x = bottomRightPoint.x - btnExit.width / 2 - margin;
        // btnExit.y = topLeftPoint.y + btnExit.height / 2 + margin;

    }

    private handleClickShowNeedles(): void {
        this.buyingInProcess = true;
        this.updateButtons();

        this.actionsCount++;

        if (this.adsLogic.isReady && this.adsLogic.adsLogic.hasAdBlock()) {
            this.gameLogic.modalsManager.hide();
            this.gameLogic.modalsManager.show(ModalsNames.confirm, <IConfirmationsConfirmControllerOptions>{
                oneButtonMode: true,
                text: LocalizationContents.getText('Adblock'),
                yesText: LocalizationContents.getText('Ok'),
                yesClick: () => {
                    this.gameLogic.modalsManager.hide()
                    this.buyingInProcess = false;
                    this.updateButtons();
                },
            });
            return;
        }

        this.showConfirm()
            .then(() => {
                return this.showAdvertisment();
            })
            .then(() => {
                this.modelEvents.dispatch(this.model, GameEvent.makePurchase, {
                    sceneName: "Game",
                    popupName: "Shop",
                    name: "boosterNeedle",
                    type: "ads"
                })
                return this.showReward({
                    forAds: false,
                    type: ConfirmationsYouRewardType.needle,
                    value: 3,
                });
            })
            .then(() => {
                this.itemsCount++;

                this.gameLogic.addBooster(BoosterTypeEnum.needle, 3);

                this.sendAnalyticBuyBooster(BoosterTypeEnum.needle, 'ads');

                this.hideConfirm();
                this.hideReward();
            })
            .catch((e) => {
                this.showError();
                throw e;
            })
            .finally(() => {
                if (this.adsLogic.isReady)
                    this.adsLogic.adsLogic.restartRewardedAdTimeout(REWARDED_ADS_TYPE.SHOP_NEEDLES);

                this.buyingInProcess = false;
                this.updateButtons();
            })
    }

    private handleClickShowWool(): void {
        this.buyingInProcess = true;
        this.updateButtons();

        this.actionsCount++;

        if (this.adsLogic.isReady && this.adsLogic.adsLogic.hasAdBlock()) {
            this.gameLogic.modalsManager.hide();
            this.gameLogic.modalsManager.show(ModalsNames.confirm, <IConfirmationsConfirmControllerOptions>{
                oneButtonMode: true,
                text: LocalizationContents.getText('Adblock'),
                yesText: LocalizationContents.getText('Ok'),
                yesClick: () => {
                    this.gameLogic.modalsManager.hide()
                    this.buyingInProcess = false;
                    this.updateButtons();
                },
            });
            return;
        }

        this.showConfirm()
            .then(() => {
                return this.showAdvertisment();
            })
            .then(() => {
                this.modelEvents.dispatch(this.model, GameEvent.makePurchase, {
                    sceneName: "Game",
                    popupName: "Shop",
                    name: "boosterYarn",
                    type: "ads"
                })
                return this.showReward({
                    forAds: false,
                    type: ConfirmationsYouRewardType.yarn,
                    value: 3,
                });
            })
            .then(() => {
                this.itemsCount++;

                this.gameLogic.addBooster(BoosterTypeEnum.yarn, 3);

                this.sendAnalyticBuyBooster(BoosterTypeEnum.yarn, 'ads');

                this.hideConfirm();
                this.hideReward();
            })
            .catch((e) => {
                this.showError();
                throw e;
            })
            .finally(() => {
                if (this.adsLogic.isReady)
                    this.adsLogic.adsLogic.restartRewardedAdTimeout(REWARDED_ADS_TYPE.SHOP_WOOL);

                this.buyingInProcess = false;
                this.updateButtons();
            })
    }

    private handleClickShowBucket(): void {
        this.buyingInProcess = true;
        this.updateButtons();

        this.actionsCount++;

        if (this.adsLogic.isReady && this.adsLogic.adsLogic.hasAdBlock()) {
            this.gameLogic.modalsManager.hide();
            this.gameLogic.modalsManager.show(ModalsNames.confirm, <IConfirmationsConfirmControllerOptions>{
                oneButtonMode: true,
                text: LocalizationContents.getText('Adblock'),
                yesText: LocalizationContents.getText('Ok'),
                yesClick: () => {
                    this.gameLogic.modalsManager.hide()
                    this.buyingInProcess = false;
                    this.updateButtons();
                },
            });
            return;
        }

        this.showConfirm()
            .then(() => {
                return this.showAdvertisment();
            })
            .then(() => {
                this.modelEvents.dispatch(this.model, GameEvent.makePurchase, {
                    sceneName: "Game",
                    popupName: "Shop",
                    name: "boosterBucket",
                    type: "ads"
                })
                return this.showReward({
                    forAds: false,
                    type: ConfirmationsYouRewardType.bucket,
                    value: 3,
                });
            })
            .then(() => {
                this.itemsCount++;

                this.gameLogic.addBooster(BoosterTypeEnum.bucket, 3);

                this.sendAnalyticBuyBooster(BoosterTypeEnum.bucket, 'ads');

                this.hideConfirm();
                this.hideReward();
            })
            .catch((e) => {
                this.showError();
                throw e;
            })
            .finally(() => {
                if (this.adsLogic.isReady)
                    this.adsLogic.adsLogic.restartRewardedAdTimeout(REWARDED_ADS_TYPE.SHOP_BUCKET);

                this.buyingInProcess = false;
                this.updateButtons();
            })
    }

    private handleClickShowCombo(): void {
        this.buyingInProcess = true;
        this.updateButtons();

        this.actionsCount++;

        if (this.adsLogic.isReady && this.adsLogic.adsLogic.hasAdBlock()) {
            this.gameLogic.modalsManager.hide();
            this.gameLogic.modalsManager.show(ModalsNames.confirm, <IConfirmationsConfirmControllerOptions>{
                oneButtonMode: true,
                text: LocalizationContents.getText('Adblock'),
                yesText: LocalizationContents.getText('Ok'),
                yesClick: () => {
                    this.gameLogic.modalsManager.hide()
                    this.buyingInProcess = false;
                    this.updateButtons();
                },
            });
            return;
        }

        this.showConfirm()
            .then(() => {
                return this.showAdvertisment();
            })
            .then(() => {
                this.modelEvents.dispatch(this.model, GameEvent.makePurchase, {
                    sceneName: "Game",
                    popupName: "Shop",
                    name: "comboYarnAndNeedleAndBucket",
                    type: "ads"
                })
                return this.showReward({
                    forAds: false,
                    type: ConfirmationsYouRewardType.combo_3x,
                    value: [1, 1, 1],
                });
            })
            .then(() => {
                this.itemsCount += 3;

                this.gameLogic.addBooster(BoosterTypeEnum.yarn, 1);
                this.gameLogic.addBooster(BoosterTypeEnum.needle, 1);
                this.gameLogic.addBooster(BoosterTypeEnum.bucket, 1);

                this.sendAnalyticBuyBooster(BoosterTypeEnum.needle, 'ads');
                this.sendAnalyticBuyBooster(BoosterTypeEnum.yarn, 'ads');
                this.sendAnalyticBuyBooster(BoosterTypeEnum.bucket, 'ads');

                this.saveOnceAtLevel();
                this.updateButtons();
                this.hideConfirm();
                this.hideReward();
            })
            .catch((error) => {
                this.showError();
                throw error;
            })
            .finally(() => {
                if (this.adsLogic.isReady)
                    this.adsLogic.adsLogic.restartRewardedAdTimeout(REWARDED_ADS_TYPE.SHOP_COMBO);

                this.buyingInProcess = false;
                this.updateButtons();
            })
    }

    private handleClickBuyCombo(): void {
        this.buyingInProcess = true;
        this.updateButtons();

        this.actionsCount++;

        this.showConfirm()
            .then(() => {
                return this.buySomething();
            })
            .then(() => {
                const price = this.gameLogic.balanceManager.getShopItemPrice(BalanceShopItemTypeEnum.combo);
                this.gameLogic.addBalanceCoins(-price);

                this.modelEvents.dispatch(this.model, GameEvent.makePurchase, {
                    sceneName: "Game",
                    popupName: "Shop",
                    name: "comboYarnAndNeedle",
                    type: "coins",
                    coins: price
                });

                return this.showReward({
                    forAds: false,
                    type: ConfirmationsYouRewardType.combo,
                    value: [3, 3],
                });
            })
            .catch(() => {

            })
            .finally(() => {
                this.itemsCount += 6;

                this.gameLogic.addBooster(BoosterTypeEnum.needle, 3);
                this.gameLogic.addBooster(BoosterTypeEnum.yarn, 3);

                //todo: why it's different events? 0_o
                this.sendAnalyticBuyBooster(BoosterTypeEnum.needle, 'coins');
                this.sendAnalyticBuyBooster(BoosterTypeEnum.yarn, 'coins');

                this.hideConfirm();
                this.hideReward();

                this.buyingInProcess = false;
                this.updateButtons();
            });
    }

    private showConfirm(): Promise<void> {
        return new Promise((resolve, reject) => {
            resolve();
            /*
            this._gameLogic.modalsManager.show(ModalsNames.confirm, {
                text: 'A you sure?',
                yesText: 'YES',
                noText: 'NO',
                yesClick: () => resolve(),
                noClick: () => reject(),
            });
            this.updateOverlay();
             */
        });
    }

    private hideConfirm(): void {
        //this._gameLogic.modalsManager.hide();
        //this.updateOverlay();
    }

    private showReward({ forAds, type, value }): Promise<void> {
        return new Promise((resolve, reject) => {
            this.updateButtons();

            const exit = () => {
                //this.analytics.sendEvent( EventEnum.showReward)
                resolve();
            };

            this.gameLogic.modalsManager.show(ModalsNames.youReward, {
                type: type,
                value: value,
                text: LocalizationContents.getText('Your Reward'),
                okText: LocalizationContents.getText('Thanks'),
                okClick: () => exit(),
                exitClick: () => exit(),
            });
        });
    }

    private showError(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.updateButtons();

            this.gameLogic.modalsManager.show(ModalsNames.confirm, {
                oneButtonMode: true,
                text: LocalizationContents.getText('Something wrong with ads'),
                yesText: LocalizationContents.getText('Ok'),
                yesClick: () => {
                    resolve();
                    this.gameLogic.modalsManager.hide()
                    this.updateButtons();
                },
            })
        })
    }

    private hideReward(): void {
        this.gameLogic.modalsManager.hide();
    }

    private async showAdvertisment(): Promise<void> {
        if (!this.adsLogic.isReady) return Promise.reject("Ads no ready");
        this.soundManager.muteSoundsByType(true, SoundType.ALL);
        return this.adsLogic.adsLogic.requestAd()
            .finally(() => {
                this.soundManager.muteSoundsByType(false, SoundType.ALL);
            });
    }

    private buySomething(): Promise<void> {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve();
            }, 1000);
        });
    }

    private sendAnalyticBuyBooster(boosterID: number, method: string): void {
        /*
        this.analytics.sendEvent(AnalyticsEventEnum.buyBooster, {
            booster_id: boosterID,
            method_id: method,
            user_id: this.$model.userModel.id,
        });
         */
    }
}
